import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import showNotification from '../component/Notification';
import baseURL from '../component/baseURL';
import stringsoflanguages from '../component/stringsoflanguages';

function viewMySharedScrollKeys(receiverId, requestId) {
    const tag = `${encodeURIComponent(String(receiverId ?? ''))}_${encodeURIComponent(String(requestId ?? ''))}`;
    return {
        scroll: `yesmls_viewMyShared_gridScrollTop_${tag}`,
        pending: `yesmls_viewMyShared_restoreScrollPending_${tag}`,
    };
}

export default function ViewMyShared({ hideMobileHeader = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [userID, setUserID] = useState(null);
    const [loading, setLoading] = useState(true);
    const [memberDetails, setMemberDetails] = useState(null);
    const [listings, setListings] = useState([]);
    const [acceptedOverrideByListing, setAcceptedOverrideByListing] = useState({});
    const [publishedOverrideByListing, setPublishedOverrideByListing] = useState({});
    const receiverId = location.state?.receiver_id ?? '';
    const requestId = location.state?.request_id ?? '';
    const gridScrollKeys = useMemo(
        () => viewMySharedScrollKeys(receiverId, requestId),
        [receiverId, requestId]
    );
    const listScrollRef = useRef(null);
    const [gridHiddenForScrollRestore, setGridHiddenForScrollRestore] = useState(() => {
        try {
            if (typeof sessionStorage === 'undefined') return false;
            return sessionStorage.getItem(viewMySharedScrollKeys(receiverId, requestId).pending) === '1';
        } catch {
            return false;
        }
    });
    const [isMobile, setIsMobile] = useState(false);
    const [gearMenuOpen, setGearMenuOpen] = useState(false);
    const [gearSpinning, setGearSpinning] = useState(false);
    const gearMenuRef = useRef(null);
    const headerTitle = useMemo(() => stringsoflanguages.viewMyShared, []);

    useEffect(() => {
        try {
            setGridHiddenForScrollRestore(sessionStorage.getItem(gridScrollKeys.pending) === '1');
        } catch {
            setGridHiddenForScrollRestore(false);
        }
    }, [gridScrollKeys.pending]);

    const normalizeImageUrl = (url) => {
        if (!url) return '';
        const s = String(url).trim();
        if (!s) return '';
        return s.startsWith('http://') ? `https://${s.slice('http://'.length)}` : s;
    };

    const memberAvatarSrc = normalizeImageUrl(memberDetails?.user_image_thumb ?? memberDetails?.user_image) || 'https://placehold.co/96x96';
    const memberCompanySrc = normalizeImageUrl(memberDetails?.company_image_thumb ?? memberDetails?.company_image) || 'https://placehold.co/108x60?text=Logo';

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        setUserID(userid);
        fetchViewMyShared(userid, receiverId, requestId);
    }, [receiverId, requestId]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        const handleScreenChange = (event) => {
            setIsMobile(event.matches);
        };

        setIsMobile(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleScreenChange);

        return () => {
            mediaQuery.removeEventListener('change', handleScreenChange);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) {
                setGearMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (loading) return undefined;
        const el = listScrollRef.current;
        if (!el) return undefined;
        let timeoutId;
        const onScroll = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                try {
                    sessionStorage.setItem(gridScrollKeys.scroll, String(el.scrollTop));
                } catch {
                    /* ignore */
                }
            }, 120);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            clearTimeout(timeoutId);
            el.removeEventListener('scroll', onScroll);
        };
    }, [loading, gridScrollKeys.scroll]);

    useLayoutEffect(() => {
        if (loading) return undefined;
        try {
            if (sessionStorage.getItem(gridScrollKeys.pending) !== '1') {
                setGridHiddenForScrollRestore(false);
                return undefined;
            }
        } catch {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        let raw;
        try {
            raw = sessionStorage.getItem(gridScrollKeys.scroll);
        } catch {
            raw = null;
        }
        if (raw == null || raw === '') {
            try {
                sessionStorage.removeItem(gridScrollKeys.pending);
            } catch {
                /* ignore */
            }
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const y = Number(raw);
        if (!Number.isFinite(y) || y < 0) {
            try {
                sessionStorage.removeItem(gridScrollKeys.pending);
            } catch {
                /* ignore */
            }
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const el = listScrollRef.current;
        if (!el) {
            try {
                sessionStorage.removeItem(gridScrollKeys.pending);
            } catch {
                /* ignore */
            }
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        el.scrollTop = y;
        try {
            sessionStorage.removeItem(gridScrollKeys.pending);
        } catch {
            /* ignore */
        }

        if (reducedMotion) {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        requestAnimationFrame(() => {
            setGridHiddenForScrollRestore(false);
        });
        return undefined;
    }, [loading, listings.length, gridScrollKeys.pending, gridScrollKeys.scroll]);

    function saveViewMySharedGridScrollAndGoToDetails(listing, id) {
        const el = listScrollRef.current;
        try {
            if (el) {
                sessionStorage.setItem(gridScrollKeys.scroll, String(el.scrollTop));
            }
            sessionStorage.setItem(gridScrollKeys.pending, '1');
        } catch {
            /* ignore */
        }
        navigate('/portfolio-details', {
            state: {
                listingID: listing.listing_id ?? listing.ListingId ?? id,
                from: 'incoming-member-feed',
            },
        });
    }

    function fetchViewMyShared(userid, receiverId, requestId) {
        let formdata = new FormData();
        formdata.append("user_id", userid)
        formdata.append("receiver_id", receiverId)
        formdata.append("request_id", requestId)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")

        fetch(baseURL.URL + '/portfolio_feed/view_feed_my_shared', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server?.code === 200) {
                    const result = responseJson.response?.result ?? {};
                    const md = result.member_details ?? null;
                    const available = Array.isArray(result.available_listings) ? result.available_listings : [];
                    setMemberDetails(md);
                    setListings(available);
                    setAcceptedOverrideByListing({});
                    setPublishedOverrideByListing({});
                    setLoading(false);
                }
                else {
                    showNotification('Error', responseJson.server?.message || 'Failed to load feed', 'danger');
                    setMemberDetails(null);
                    setListings([]);
                    setAcceptedOverrideByListing({});
                    setPublishedOverrideByListing({});
                    setLoading(false);
                }
            })
            .catch((error) => {
                showNotification('Error', error.message, 'danger');
                setMemberDetails(null);
                setListings([]);
                setAcceptedOverrideByListing({});
                setPublishedOverrideByListing({});
                setLoading(false);
            });
    }


    function acceptDeclineFeed(nextValue, listingId) {
        if (!userID || !requestId || !listingId) return Promise.reject(new Error('Missing required data'));
        let formdata = new FormData();
        formdata.append("user_id", userID)
        formdata.append("response_type", String(nextValue))
        formdata.append("request_id", requestId)
        formdata.append("listing_id", listingId)
        formdata.append("on_off_toggle", String(nextValue))
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        return fetch(baseURL.URL + '/portfolio_feed/accept_decline_feed_shared_single', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server?.code === 200) {
                    showNotification('Success', responseJson.server?.message || 'Updated', 'success');
                    fetchViewMyShared(userID, receiverId, requestId);
                    return true;
                }
                const msg = responseJson.server?.message || 'Failed to update';
                showNotification('Error', msg, 'danger');
                throw new Error(msg);
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', error.message, 'danger');
                throw error;
            });
    }


    function publishUnpublishFeed(nextValue, listingId) {
        if (!userID || !requestId || !listingId) return Promise.reject(new Error('Missing required data'));
        let formdata = new FormData();
        formdata.append("user_id", userID)
        formdata.append("is_published", String(nextValue))
        formdata.append("request_id", requestId)
        formdata.append("listing_id", listingId)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")

        return fetch(baseURL.URL + '/portfolio_feed/publish_unpublish_feed_shared', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server?.code === 200) {
                    showNotification('Success', responseJson.server?.message || 'Updated', 'success');
                    fetchViewMyShared(userID, receiverId, requestId);
                    return true;
                }
                const msg = responseJson.server?.message || 'Failed to update';
                showNotification('Error', msg, 'danger');
                throw new Error(msg);
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', error.message, 'danger');
                throw error;
            });
    }

    function handleAcceptedToggle(listing, nextChecked) {
        const listingId = listing?.listing_id;
        const nextValue = nextChecked ? '1' : '0';
        const prevChecked = acceptedOverrideByListing?.[listingId] ?? (String(listing?.status) === '1');

        setAcceptedOverrideByListing((prev) => ({ ...prev, [listingId]: nextChecked }));
        acceptDeclineFeed(nextValue, listingId).catch(() => {
            setAcceptedOverrideByListing((prev) => ({ ...prev, [listingId]: prevChecked }));
        });
    }

    function handlePublishedToggle(listing, nextChecked) {
        const listingId = listing?.listing_id;
        const nextValue = nextChecked ? '1' : '0';
        const prevChecked = publishedOverrideByListing?.[listingId] ?? (String(listing?.is_published) === '1');

        setPublishedOverrideByListing((prev) => ({ ...prev, [listingId]: nextChecked }));
        publishUnpublishFeed(nextValue, listingId).catch(() => {
            setPublishedOverrideByListing((prev) => ({ ...prev, [listingId]: prevChecked }));
        });
    }

    const connectionFeedMemberId = memberDetails?.user_id ?? receiverId ?? '';
    function handleViewMySharedBack() {
        const returnTo = location.state?.returnTo;
        if (returnTo?.path) {
            navigate(returnTo.path, { replace: true, state: returnTo.state ?? {} });
            return;
        }
        navigate('/feed/my-feed-shared', { replace: true });
    }

    function navigateToConnectionFeedSettings() {
        setGearMenuOpen(false);
        if (!connectionFeedMemberId) {
            showNotification('Error', 'Member information is not available', 'danger');
            return;
        }
        navigate('/feed/connection-feed-settings', {
            state: {
                member_id: connectionFeedMemberId,
                memberID: connectionFeedMemberId,
                user_details: memberDetails,
                member: memberDetails,
                returnTo: {
                    path: '/feed/view-my-shared',
                    state: {
                        receiver_id: receiverId,
                        request_id: requestId,
                        returnTo: location.state?.returnTo ?? {
                            path: '/feed/my-feed-shared',
                            state: {},
                        },
                    },
                },
            },
        });
    }

    return (
        <React.Fragment>
            {!hideMobileHeader && isMobile && <div className="mobile-page-header incoming-member-feed-header view-my-shared-mobile-header flex align-items-center">
                <button
                    type="button"
                    className="mobile-back-btn"
                    onClick={handleViewMySharedBack}
                    aria-label="Back"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <h2 className="mobile-page-title incoming-member-feed-title">{headerTitle}</h2>
            </div>}

            <div className="content-right incoming-member-feed-page">


                <div className="incoming-member-feed-body">
                    {loading ? (
                        <div className="page-loader-wrapper" style={{ padding: 16 }}>
                            <div className="react-js-loader">
                                <div className="react-js-loader-spinner"></div>
                                <p className="react-js-loader-text">{stringsoflanguages.loading}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="incoming-member-feed-content flex gap-4 w-full mb-4">
                            <div className="incoming-member-profile-card w-full">
                                <div className="incoming-member-profile-left">
                                    <div className="incoming-member-avatar">
                                        <img
                                            src={memberDetails?.user_image_thumb}
                                            alt={memberDetails?.user_name ?? 'Member'}
                                        // referrerPolicy="no-referrer"
                                        // crossOrigin="anonymous"
                                        // onError={(e) => {
                                        //     e.currentTarget.onerror = null;
                                        //     e.currentTarget.src = 'https://placehold.co/96x96';
                                        // }}
                                        />
                                    </div>
                                    <div className="incoming-member-meta">
                                        <div className="incoming-member-name">{memberDetails?.user_name ?? '—'}</div>
                                        <div className="incoming-member-location">
                                            <i className="fa-solid fa-location-dot" aria-hidden="true" />
                                            <span>{[memberDetails?.city_name, memberDetails?.country_name].filter(Boolean).join(' - ') || '—'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="incoming-member-profile-right">
                                    <img
                                        src={memberDetails?.company_image_thumb ?? memberDetails?.company_image ?? 'https://placehold.co/108x60?text=Logo'}
                                        alt="Company"
                                        style={{ width: 54, height: 30, objectFit: 'contain' }}
                                    // referrerPolicy="no-referrer"
                                    // crossOrigin="anonymous"
                                    // onError={(e) => {
                                    //     e.currentTarget.onerror = null;
                                    //     e.currentTarget.src = 'https://placehold.co/108x60?text=Logo';
                                    // }}
                                    />
                                </div>
                            </div>


                            <div className="incoming-member-stats w-full">
                                <div className="incoming-member-stat">
                                    <div className="incoming-member-stat-number">{memberDetails?.total_published ?? 0}</div>
                                    <div className="incoming-member-stat-label">{stringsoflanguages.totalPublished}</div>
                                </div>
                                <div className="incoming-member-stat-divider" aria-hidden="true" />
                                <div className="incoming-member-stat">
                                    <div className="incoming-member-stat-number">{memberDetails?.total_accepted ?? 0}</div>
                                    <div className="incoming-member-stat-label">{stringsoflanguages.totalAccepted}</div>
                                </div>
                                <div className="incoming-member-stat-divider" aria-hidden="true" />
                                <div className="incoming-member-stat">
                                    <div className="incoming-member-stat-number">{memberDetails?.total_shared ?? 0}</div>
                                    <div className="incoming-member-stat-label">{stringsoflanguages.totalShared}</div>
                                </div>
                            </div>

                        </div>
                    )}







                    <div
                        className={`my-listings-cards-grid w-full${gridHiddenForScrollRestore ? ' gm-grid--restore-hidden' : ''}`}
                        ref={listScrollRef}
                    >
                        <div className="property-grid">
                            {listings.length === 0 ? (
                                <p className="no-data-message">{stringsoflanguages.noListingsFound}</p>
                            ) : (
                                listings.map((listing, index) => {
                                    const id = listing.listing_id ?? listing.feed_id ?? listing.request_id ?? index;
                                    const image = listing.image ?? 'https://placehold.co/600x400';
                                    const title = listing.ListingId ?? listing.listing_id ?? '—';
                                    const propertyType = listing.PropertyType ?? 'Property Type';
                                    const streetPart = [listing.StreetNumber, listing.StreetName].filter(Boolean).join(' ').trim();
                                    const cityParts = [listing.city_name, listing.state_name, listing.PostalCode, listing.country_name].filter(Boolean);
                                    const cityPart = cityParts.join(', ').trim();
                                    const address = streetPart ? (cityPart ? `${streetPart}, ${cityPart}` : streetPart) : (cityPart || '—');
                                    const areaValue = listing.LivingArea ?? listing.LotSizeArea ?? null;
                                    const rawUnit = listing.LivingAreaUnits ?? listing.LotSizeUnits ?? 'Sq Feet';
                                    const areaUnitDisplay = /meter|metre/i.test(String(rawUnit)) ? 'm²' : 'ft²';
                                    const area = areaValue != null && areaValue !== '' ? `${areaValue} ${areaUnitDisplay}` : '—';
                                    const bedrooms = listing.BedroomsTotal ?? '—';
                                    const bathrooms = listing.BathroomsTotalInteger ?? '—';
                                    const parking = listing.ParkingTotal ?? listing.GarageSpaces ?? '—';
                                    const price = listing.ListPrice ?? '—';
                                    const status = (listing.StandardStatus ?? listing.StandardStatusName) || '—';
                                    const acceptedChecked = acceptedOverrideByListing?.[listing.listing_id] ?? (String(listing.status) === '1');
                                    const publishedChecked = publishedOverrideByListing?.[listing.listing_id] ?? (String(listing.is_published) === '1');

                                    const agent = {
                                        avatar: memberDetails?.user_image_thumb ?? memberDetails?.user_image ?? '',
                                        name: memberDetails?.user_name ?? ([memberDetails?.fname, memberDetails?.lname].filter(Boolean).join(' ').trim() || '—')
                                    };

                                    return (
                                        <>



                                            <div
                                                key={id}
                                                className="property-card cursor-pointer"
                                                onClick={() => saveViewMySharedGridScrollAndGoToDetails(listing, id)}
                                            >

                                                <div className="property-image">
                                                    <img src={image} alt="property" />
                                                    <div className="agent-info">
                                                        <div className="agent-avatar">
                                                            <img src={agent.avatar || 'https://placehold.co/40x40'} alt="agent" />
                                                        </div>
                                                        <div className="agent-name">
                                                            <span>{agent.name}</span>
                                                        </div>
                                                    </div>
                                                    <div className="status">
                                                        {status === 'Active' && (
                                                            <>
                                                                <span className="status-dot active"></span>
                                                                <span>{status}</span>
                                                            </>
                                                        )}
                                                        {status === 'Coming Soon' && (
                                                            <>
                                                                <span className="status-dot coming-soon"></span>
                                                                <span>{status}</span>
                                                            </>
                                                        )}
                                                        {status === 'Sold' && (
                                                            <>
                                                                <span className="status-dot sold"></span>
                                                                <span>{status}</span>
                                                            </>
                                                        )}
                                                        {status === 'Cancelled' && (
                                                            <>
                                                                <span className="status-dot cancelled"></span>
                                                                <span>{status}</span>
                                                            </>
                                                        )}
                                                        {status === 'Under Offer' && (
                                                            <>
                                                                <span className="status-dot under-offer"></span>
                                                                <span>{status}</span>
                                                            </>
                                                        )}
                                                        {(!status || !['Active', 'Coming Soon', 'Sold', 'Cancelled', 'Under Offer'].includes(status)) && (
                                                            <>
                                                                <span className="status-dot"></span>
                                                                <span>{status || 'N/A'}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="property-content">
                                                    <h3 className="property-title">{title} </h3>
                                                    <div className="location">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10.0001 1.81818C6.49118 1.81818 3.63647 4.47669 3.63647 7.74444C3.63647 11.7998 9.33131 17.7533 9.57377 18.0048C9.80151 18.241 10.1991 18.2406 10.4265 18.0048C10.6689 17.7533 16.3637 11.7998 16.3637 7.74444C16.3637 4.47669 13.509 1.81818 10.0001 1.81818ZM10.0001 10.7261C8.23468 10.7261 6.79844 9.38854 6.79844 7.74444C6.79844 6.10034 8.23472 4.7628 10.0001 4.7628C11.7655 4.7628 13.2017 6.10037 13.2017 7.74447C13.2017 9.38857 11.7655 10.7261 10.0001 10.7261Z" fill="black" fillOpacity="0.35" />
                                                        </svg>
                                                        <span>{address}</span>
                                                    </div>
                                                    <div className="property-details">
                                                        <div className="detail-row">
                                                            <div className="detail-item">
                                                                <i className="fa-solid fa-ruler-combined"></i>
                                                                <span>{area}</span>
                                                            </div>
                                                            <div className="detail-item">
                                                                <i className="fa-solid fa-bed"></i>
                                                                <span>{bedrooms}</span>
                                                            </div>
                                                            <div className="detail-item">
                                                                <i className="fa-solid fa-bath"></i>
                                                                <span>{bathrooms}</span>
                                                            </div>
                                                            <div className="detail-item">
                                                                <i className="fa-solid fa-car"></i>
                                                                <span>{parking}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="price-type">
                                                        <span className="price">{price !== '—' && price !== '' && !isNaN(Number(price)) ? `€ ${Number(price).toLocaleString()}` : price}</span>
                                                    </div>

                                                    <div
                                                        className="incoming-member-feed-actions"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onTouchStart={(e) => e.stopPropagation()}
                                                    >
                                                        <div className="incoming-member-toggle">
                                                            <span>{stringsoflanguages.accepted}</span>
                                                            <button
                                                                type="button"
                                                                className={`imf-check-btn ${acceptedChecked ? 'is-active' : ''}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAcceptedToggle(listing, !acceptedChecked);
                                                                }}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onTouchStart={(e) => e.stopPropagation()}
                                                                aria-label={stringsoflanguages.accepted}
                                                                aria-pressed={acceptedChecked}
                                                            >
                                                                <i className="fa-solid fa-check" />
                                                            </button>
                                                        </div>
                                                        {
                                                            listing.business_type == "2" ?
                                                                <div className="incoming-member-b2c-pill" onClick={(e) => e.stopPropagation()}>B to C</div>
                                                                :
                                                                <div className="incoming-member-b2b-pill" onClick={(e) => e.stopPropagation()}>B to B</div>

                                                        }
                                                        <div className="incoming-member-toggle">
                                                            <span>{stringsoflanguages.published}</span>
                                                            <button
                                                                type="button"
                                                                className={`imf-check-btn ${publishedChecked ? 'is-active' : ''}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePublishedToggle(listing, !publishedChecked);
                                                                }}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onTouchStart={(e) => e.stopPropagation()}
                                                                aria-label={stringsoflanguages.published}
                                                                aria-pressed={publishedChecked}
                                                            >
                                                                <i className="fa-solid fa-check" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {!isMobile && (
                        <div className="btn-section btn-section-gear view-my-shared-gear" ref={gearMenuRef}>
                            <button
                                type="button"
                                className={`btn-primary gear-btn ${gearSpinning ? 'gear-spin' : ''}`}
                                onClick={() => {
                                    setGearMenuOpen((prev) => !prev);
                                    setGearSpinning(true);
                                    setTimeout(() => setGearSpinning(false), 500);
                                }}
                                aria-label="Options"
                                aria-expanded={gearMenuOpen}
                            >
                                <i className="fa-solid fa-gear"></i>
                            </button>
                            {gearMenuOpen && (
                                <ul className="gear-dropdown" aria-label="View my shared options">
                                    <li>
                                        <button
                                            type="button"
                                            className="gear-dropdown-item"
                                            onClick={navigateToConnectionFeedSettings}
                                        >
                                            <i className="fa-regular fa-pen-to-square" />
                                            <span>{stringsoflanguages.edit}</span>
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </div>
                    )}

                    {isMobile && (
                        <nav
                            className="mobile-bottom-nav liquidGlass-wrapper dock view-my-shared-mobile-bottom-nav"
                            aria-label="View my shared actions"
                        >
                            <div className="liquidGlass-effect" aria-hidden="true" />
                            <div className="liquidGlass-shine" aria-hidden="true" />
                            <div
                                className="mobile-nav-item"
                                role="button"
                                tabIndex={0}
                                aria-label={stringsoflanguages.edit}
                                onClick={navigateToConnectionFeedSettings}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        navigateToConnectionFeedSettings();
                                    }
                                }}
                            >
                                <i className="fa-regular fa-pen-to-square"></i>
                            </div>
                        </nav>
                    )}

                </div>
            </div>
        </React.Fragment>
    );
}

